/**
 * Particle smoke — Casa Aura (Variant D)
 *
 * The original particle system approach — the version that produced
 * the "getting closer" feedback. Discrete soft-circle particles with:
 *   — Long laminar column (rises straight for 58% of height)
 *   — K-H transition: gentle sway begins mid-column
 *   — Analytical curl noise for turbulent plume at top
 *   — Near-white particle colors, long particle life
 */
(function (root) {
  'use strict';
  if (root.SmokeParticle) return;

  // Analytical curl noise: u = ∂Ψ/∂y, v = −∂Ψ/∂x
  var _C = [
    { kx:  0.007, ky:  0.005, w:  0.016, A: 1.00 },
    { kx:  0.014, ky: -0.010, w:  0.024, A: 0.50 },
    { kx: -0.005, ky:  0.018, w: -0.012, A: 0.28 },
  ];

  function curlAt(px, py, t) {
    var u = 0, v = 0, c, ct;
    for (var i = 0; i < 3; i++) {
      ct = _C[i]; c = Math.cos(ct.kx * px + ct.ky * py + ct.w * t);
      u += ct.A * ct.ky * c; v -= ct.A * ct.kx * c;
    }
    return { x: u * 110, y: v * 110 };
  }

  function Particle(ox, oy, id) {
    this.x  = ox + (Math.random() - 0.5) * 2;
    this.y  = oy;
    this.oy = oy;
    this.vx = (Math.random() - 0.5) * 0.10;
    this.vy = -(Math.random() * 0.38 + 0.32);
    this.life    = 0;
    this.maxLife = Math.floor(Math.random() * 250 + 750);
    this.phaseA  = Math.random() * Math.PI * 2;
    this.phaseB  = Math.random() * Math.PI * 2;
    this.khPhase = id * 0.09;
    this.size    = Math.random() * 1.0 + 0.5;
    this.finalSize = Math.random() * 40 + 20;
    var cool = Math.random() * 14;
    this.cr = 220 + Math.floor(Math.random() * 18) - cool;
    this.cg = 224 + Math.floor(Math.random() * 14) - cool * 0.3;
    this.cb = 230 + Math.floor(Math.random() * 14) + cool * 0.6;
    this.maxOpacity = 0.06 + Math.random() * 0.10;
    this.opacity = 0;
    this.rotation = Math.random() * Math.PI * 2;
    this.rotSpeed = (Math.random() - 0.5) * 0.009;
    this.eW = 0.72 + Math.random() * 0.50;
  }

  Particle.prototype.update = function (t, canvasH) {
    this.life++;
    var progress = this.life / this.maxLife;
    var rise = Math.max(0, (this.oy - this.y) / canvasH);

    // Laminar until 58%; K-H + curl after
    var tBlend = Math.max(0, Math.min(1, (rise - 0.58) / 0.24));
    tBlend = tBlend * tBlend * tBlend;

    var khAmp  = 14 * tBlend * (1 - Math.max(0, (tBlend - 0.65) / 0.35));
    var khVx   = Math.sin(t * 0.016 + this.khPhase) * khAmp * 0.005;
    var curl   = curlAt(this.x, this.y, t);
    var curlX  = curl.x * tBlend * 0.025;
    var micro  = (Math.sin(t * 0.033 + this.phaseA) + Math.cos(t * 0.021 + this.phaseB)) * 0.08 * tBlend;

    this.vx += khVx + curlX + micro * 0.015;
    this.vx *= 0.920;
    this.vy *= 0.999;
    this.x  += this.vx;
    this.y  += this.vy;
    this.rotation += this.rotSpeed;
    this.size += (this.finalSize - this.size) * 0.0065;

    if (progress < 0.05) {
      this.opacity = this.maxOpacity * (progress / 0.05);
    } else if (progress < 0.84) {
      this.opacity = this.maxOpacity;
    } else {
      this.opacity = this.maxOpacity * (1 - (progress - 0.84) / 0.16);
    }
  };

  Particle.prototype.draw = function (ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);
    var op  = this.opacity.toFixed(3);
    var op2 = (this.opacity * 0.52).toFixed(3);
    var g   = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size);
    g.addColorStop(0,    'rgba(' + this.cr + ',' + this.cg + ',' + this.cb + ',' + op + ')');
    g.addColorStop(0.40, 'rgba(' + (this.cr-8) + ',' + (this.cg-5) + ',' + (this.cb-3) + ',' + op2 + ')');
    g.addColorStop(1,    'rgba(' + (this.cr-22) + ',' + (this.cg-15) + ',' + (this.cb-8) + ',0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(0, 0, this.size, this.size * this.eW, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  Object.defineProperty(Particle.prototype, 'dead', {
    get: function () { return this.life >= this.maxLife || this.y < -180; }
  });

  function SmokeParticle(canvas, opts) {
    this.canvas    = canvas;
    this.ctx       = canvas.getContext('2d');
    this.opts      = Object.assign({ autoplay: true, showEmber: true }, opts || {});
    this.particles = [];
    this.time      = 0;
    this.frame     = 0;
    this.emitCount = 0;
    this.running   = false;
    this._raf      = null;

    this._resize = this._resize.bind(this);
    this._tick   = this._tick.bind(this);
    window.addEventListener('resize', this._resize);
    this._resize();
    if (this.opts.autoplay) this._setupIO();
  }

  SmokeParticle.prototype._resize = function () {
    var p = this.canvas.parentElement;
    this.canvas.width  = p.offsetWidth;
    this.canvas.height = parseInt(this.canvas.dataset.height, 10) || 400;
    this.ox = Math.round(this.canvas.width  / 2);
    this.oy = Math.round(this.canvas.height * 0.93);
    var cap = this.canvas.width < 580 ? 140 : 260;
    this._cap = cap;
  };

  SmokeParticle.prototype._setupIO = function () {
    var self = this;
    new IntersectionObserver(function (e) {
      e.forEach(function (en) { en.isIntersecting ? self.start() : self.stop(); });
    }, { threshold: 0.05 }).observe(this.canvas);
  };

  SmokeParticle.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._raf = requestAnimationFrame(this._tick);
  };

  SmokeParticle.prototype.stop = function () {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  };

  SmokeParticle.prototype._tick = function () {
    if (!this.running) return;
    this.time++;
    this.frame++;

    if (this.frame % 1 === 0) {
      var n = Math.min(5, this._cap - this.particles.length);
      for (var i = 0; i < n; i++) {
        this.particles.push(new Particle(this.ox, this.oy, this.emitCount++));
      }
    }

    var h = this.canvas.height;
    for (var j = this.particles.length - 1; j >= 0; j--) {
      this.particles[j].update(this.time, h);
      if (this.particles[j].dead) this.particles.splice(j, 1);
    }

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, h);

    if (this.opts.showEmber) {
      var g = ctx.createRadialGradient(this.ox, this.oy, 0, this.ox, this.oy, 12);
      g.addColorStop(0,   'rgba(255,200,90,0.14)');
      g.addColorStop(0.6, 'rgba(200,85,15,0.05)');
      g.addColorStop(1,   'rgba(130,35,0,0)');
      ctx.beginPath(); ctx.fillStyle = g; ctx.arc(this.ox, this.oy, 12, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.fillStyle = 'rgba(255,230,160,0.45)'; ctx.arc(this.ox, this.oy, 1.0, 0, Math.PI*2); ctx.fill();
    }

    for (var k = 0; k < this.particles.length; k++) {
      this.particles[k].draw(ctx);
    }

    this._raf = requestAnimationFrame(this._tick);
  };

  root.SmokeParticle = SmokeParticle;
}(window));
